import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../App";
import { useI18n } from "../i18n/I18nProvider";

const SESSION_KEY = "2fa-banner-dismissed";

/**
 * TwoFactorBanner — banner persistente en AppLayout.
 *
 * Solo se muestra si user.totp_enabled === false.
 * Se puede dismissar por sesión (sessionStorage); vuelve en el próximo login.
 */
const TwoFactorBanner = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Resetear dismiss si el user cambia (ej. nuevo login)
  useEffect(() => {
    try {
      const val = sessionStorage.getItem(SESSION_KEY);
      setDismissed(val === "true");
    } catch {
      setDismissed(false);
    }
  }, [user?.user_id]);

  // No mostrar si 2FA ya activo o si fue dismissado en esta sesión
  if (!user || user.totp_enabled !== false || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "true");
    } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-amber-500/10 border-b border-amber-500/30 px-4 py-2.5 flex items-center justify-between gap-4"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 min-w-0">
        <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0" />
        <span className="text-sm text-amber-200 truncate">
          {t("auth.2fa.banner.text")}
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Button
          asChild
          size="sm"
          variant="outline"
          className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20 hover:text-amber-100 text-xs h-7"
        >
          <Link to="/account/2fa">{t("auth.2fa.banner.cta")}</Link>
        </Button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("auth.2fa.banner.dismiss")}
          className="text-amber-400/60 hover:text-amber-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TwoFactorBanner;
